import React from 'react';
import { FormattedMessage } from 'react-intl-next';
import { SectionContainer, SwitcherItemWithDropdown } from '../../primitives';
import { LozengeType } from '../../primitives/item';
import messages from './messages';
import { type SwitcherItemType } from '../../../common/utils/links';
import {
	getItemAnalyticsContext,
	NavigationAnalyticsContext,
} from '../../../common/utils/analytics';
import {
	AnalyticsItemType,
	type GetExtendedAnalyticsAttributes,
	Product,
	type GenericShowFlag,
} from '../../../types';
import { AdminSubsection } from '../../../admin/components/admin-subsection';
import { PartialResultError } from './partial-result-error';
import { useExperienceSuccess, Experience } from '../../../common/utils/experience-tracker';
import capitalize from 'lodash/capitalize';
import ErrorBoundary from '../error-boundary';
import type { JoinableSiteItemType } from '../../../cross-join/utils/cross-join-links';
import { JoinProductItem } from './join-product-item';
import { AppIcon } from '../../primitives/app-icon';
import { useIsSwitcherTeam25TestCohort } from '../experiment-provider';

type SwitchToSectionProps = {
	adminLinks: SwitcherItemType[];
	isPartialResult: boolean;
	licensedProductLinks: SwitcherItemType[];
	getExtendedAnalyticsAttributes: GetExtendedAnalyticsAttributes;
	product: Product;
	experimentalJoinableSiteLinks?: JoinableSiteItemType[];
	showFlag?: GenericShowFlag<any>;
};

const getLozenges = (item: SwitcherItemType) => {
	const lozenges: LozengeType[] = [];

	if (item.shouldShowTryLozenge) {
		lozenges.push(LozengeType.Try);
	} else if (item.shouldShowNewBoldLozenge) {
		lozenges.push(LozengeType.NewBold);
	} else if (item.shouldShowNewLozenge) {
		lozenges.push(LozengeType.New);
	}

	return lozenges;
};

const getDescription = (
	item: SwitcherItemType,
	licensedProductLinks: SwitcherItemType[],
	product: Product,
): SwitcherItemType['description'] => {
	const description = item.description;

	if (!item.shouldShowNewLozenge && !item.shouldShowTryLozenge && !item.shouldShowNewBoldLozenge) {
		return description;
	}

	if (item.isProductInactive) {
		// show different message if JSW is a licensedProduct
		const hasJSWlicensedProduct = licensedProductLinks.some(
			(link) => link.productType === 'JIRA_SOFTWARE',
		);
		const message = hasJSWlicensedProduct
			? messages.jwmInactiveSwitcherWithJSWItemDescription
			: messages.jwmInactiveSwitcherWithDefaultItemDescription;
		return <FormattedMessage {...message} />;
	}

	// TBLZ-2123: Growth - Trailblazers experiment - More info at go/trailblazerexperiments
	if (item.shouldShowNewBoldLozenge) {
		const nudge =
			product === Product.CONFLUENCE
				? messages.experimentNudgeToJiraConfluenceDescription
				: messages.experimentNudgeToJiraDescription;

		return <FormattedMessage {...nudge} />;
	}

	const message =
		messages[
			`autoProvisionedSwitcherItem${capitalize(
				item.productType,
			)}Description` as keyof typeof messages
		];

	if (message?.id) {
		return <FormattedMessage {...message} />;
	}

	return description;
};

export const SwitchToSection = ({
	adminLinks,
	isPartialResult,
	licensedProductLinks,
	getExtendedAnalyticsAttributes,
	product,
	experimentalJoinableSiteLinks,
	showFlag,
}: SwitchToSectionProps) => {
	useExperienceSuccess(Experience.RENDER_SWITCH_TO_SECTION);
	const isVisualRefreshEnabled = useIsSwitcherTeam25TestCohort();

	const renderChildren = () => {
		return [
			licensedProductLinks.map((item, groupIndex) => {
				return (
					<NavigationAnalyticsContext
						key={item.key}
						data={getItemAnalyticsContext(
							groupIndex,
							null,
							AnalyticsItemType.PRODUCT,
							item.productType,
							{
								...getExtendedAnalyticsAttributes(item.productType),
								isProductInactive: Boolean(item.isProductInactive).toString(),
								isAutoProvisioned: Boolean(item.autoProvisionDate).toString(),
							},
						)}
					>
						<SwitcherItemWithDropdown
							icon={<AppIcon iconKey={item.iconKey} testId="switcher-item-icon__product" />}
							description={getDescription(item, licensedProductLinks, product)}
							href={item.href}
							childItems={item.childItems}
							testId={`switcher-item__${item.key}`}
							lozenges={getLozenges(item)}
							shouldShowNewLozenge={item.shouldShowNewLozenge}
							shouldShowTryLozenge={item.shouldShowTryLozenge}
							shouldShowNewBoldLozenge={item.shouldShowNewBoldLozenge}
							productName={item.label}
							experimentNudgeToJira={item.experimentNudgeToJira}
						>
							{item.label}
						</SwitcherItemWithDropdown>
					</NavigationAnalyticsContext>
				);
			}),
			isPartialResult ? (
				<PartialResultError
					key="partial-result-error"
					emptyResult={licensedProductLinks.length === 0}
				/>
			) : null,
			adminLinks.length > 0 && <AdminSubsection key="admin-item" adminLinks={adminLinks} />,
		];
	};

	return (
		<SectionContainer
			sectionId="switchTo"
			title={isVisualRefreshEnabled ? undefined : <FormattedMessage {...messages.yourApps} />}
			actionSubject="atlassianLink"
		>
			{renderChildren()}
			{/* Join product experiment https://home.atlassian.com/o/2346a038-3c8c-498b-a79b-e7847859868d/s/a436116f-02ce-4520-8fbb-7301462a1674/project/ATLAS-80932 */}
			{experimentalJoinableSiteLinks &&
				(product === Product.JIRA || product === Product.CONFLUENCE) && (
					<ErrorBoundary
						product={product as Product}
						triggerSubject="JoinProductItem"
						hideFallbackUI
					>
						<JoinProductItem
							joinableSiteLinks={experimentalJoinableSiteLinks}
							showFlag={showFlag}
						/>
					</ErrorBoundary>
				)}
		</SectionContainer>
	);
};
